import { useEffect, useState } from "react";

// ui
import { Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditNoteIcon from "@mui/icons-material/EditNote";

import { SystemConfigEditModal } from "../component/systemConfig/SystemConfigEditModal";

// Bar
import TableTopBar from "../component/TableTopBar";
import AiChatSideBar from "../component/AiChatSideBar";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// API
import { getSystemConfig, putSystemConfig } from "../api/systemConfig";

// OtherFunc
import { useWindowSize } from "../lib/useWindowSize";

// プロフィール項目
const profileColumns = [
  {
    key: "password_master_account_screen",
    name: "マスターアカウント初期パスワード",
    admin: ["マスターアカウント"],
  },
  {
    key: "password_staff_account_screen",
    name: "スタッフアカウント初期パスワード",
    admin: ["マスターアカウント"],
  },
  {
    key: "chatwork_base_room_id",
    name: "ChatWork 社内通知用ルームID",
    admin: ["マスターアカウント"],
  },
  {
    key: "chatwork_api_token",
    name: "ChatWork APIトークン",
    admin: ["マスターアカウント"],
  },
  {
    key: "line_api_token",
    name: "LINE APIトークン",
    admin: ["マスターアカウント"],
  },
  {
    key: "contract_number_key",
    name: "自動付与申込番号　頭文字",
    admin: ["マスターアカウント"],
  },
];

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "1.5px 1.5px rgba(115, 115, 115, 0.8)",
    fontSize: 11,
  },
}));

function SystemList(props: {
  i: number;
  column: {
    key: string;
    name: string;
    admin: string[];
  };
  profileData:
    | {
        [key: string]: string;
      }
    | undefined;
  setEditSystemConfig: (
    data: {
      status: "before" | "editing" | "success" | "failed" | string;
      key: string;
      value: string;
      name: string;
    } | null
  ) => void;
}) {
  const { i, column, profileData, setEditSystemConfig } = props;
  const [openTooltip, setOpenTooltip] = useState(false);
  return (
    <Box key={"profileBox_" + i}>
      <Box
        className="serch-label"
        sx={{
          width: "100%",
          marginTop: "50px",
          marginLeft: "10px",
        }}
      >
        {column.name}
      </Box>
      <Box
        className="serch-label"
        sx={{
          display: "flex",
          width: "100%",
          marginLeft: "10px",
          textAlign: "right",
          marginTop: "5px",
          minHeight: "30px",
          borderBottom: "0.2px solid silver",
          wordWrap: "break-word",
          wordBreak: "break-all",
        }}
      >
        <Box
          sx={{
            width: "calc(100% - 60px)",
            textAlign: "right",
            wordWrap: "break-word",
            wordBreak: "break-all",
          }}
        >
          {profileData?.[column.key]}
        </Box>
        <LightTooltip
          onClose={() => setOpenTooltip(false)}
          open={openTooltip}
          title="コピーしました"
        >
          <IconButton
            sx={{
              marginLeft: "5px",
              height: "25px",
              width: "25px",
            }}
            aria-label="detail row"
            size="medium"
            onClick={() => {
              navigator.clipboard.writeText(
                profileData?.[column.key.replace("_screen", "")] as string
              );
              setOpenTooltip(true);
            }}
          >
            <ContentCopyIcon fontSize="medium" />
          </IconButton>
        </LightTooltip>
        <IconButton
          sx={{
            marginLeft: "5px",
            height: "25px",
            width: "25px",
          }}
          aria-label="detail row"
          size="medium"
          onClick={() => {
            setEditSystemConfig({
              status: "before",
              key: column.key.replace("_screen", ""),
              name: column.name,
              value: "",
            });
          }}
        >
          <EditNoteIcon fontSize="medium" />
        </IconButton>
      </Box>
    </Box>
  );
}

function SystemConfig() {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const { windowWidth, windowHeight } = useWindowSize();

  // プロフィール
  const [profileData, setProfileData] = useState<{ [key: string]: string }>();
  async function getProfileData() {
    setIsLoading(true);
    const detail_data = await getSystemConfig();
    setProfileData(detail_data);
    setIsLoading(false);
  }
  useEffect(() => {
    getProfileData();
  }, []);

  const [aiChatOpen, setAiChatOpen] = useState(false);

  const [editSystemConfig, setEditSystemConfig] = useState<{
    status: "before" | "editing" | "success" | "failed" | string;
    key: string;
    value: string;
    name: string;
  } | null>(null);

  async function putEditData() {
    if (editSystemConfig) {
      setIsLoading(true);
      setEditSystemConfig({
        ...editSystemConfig,
        status: "editing",
      });
      const res = await putSystemConfig(
        editSystemConfig.key,
        editSystemConfig.value
      );
      setEditSystemConfig({
        ...editSystemConfig,
        status: res,
      });
      getProfileData();
      setIsLoading(false);
    }
  }
  return (
    <Box
      data-testid="system-config-page"
      style={{
        width: isSmartPhone
          ? "calc(100% - 31px)"
          : aiChatOpen
          ? "calc(100% - 331px)"
          : "calc(100% - 31px)",
        height: isSmartPhone ? "calc(100vh - 120px)" : "calc(100vh - 40px)",
      }}
    >
      <TableTopBar
        serch={false}
        serchOpen={false}
        setSerchOpen={() => {}}
        addDataNames={[]}
        addDataName={""}
        setAddDataName={() => {}}
        addCSVDataNames={[]}
        addCSVDataName={""}
        setAddCSVDataName={() => {}}
        getXLSXDataNames={[]}
        getXLSXDataName={""}
        setGetXLSXDataName={() => {}}
        editDatasNames={[]}
        editDatasName={""}
        setEditDatasName={() => {}}
        editIDs={[]}
        columnSettingOpen={false}
        openColumnSettingWindow={() => {}}
        aiChatOpen={aiChatOpen}
        setAiChatOpen={setAiChatOpen}
      />
      <Box
        style={{
          marginTop: isSmartPhone ? "90px" : "10px",
          marginLeft: windowWidth < 800 ? "10px" : "calc(50% - 380px)",
          width: windowWidth < 800 ? "calc(100% - 20px)" : "760px",
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          component="div"
          position={"static"}
          style={{
            width: "100%",
          }}
        >
          <Box
            className="shippori-antique-regular"
            sx={{
              display: "inline",
              fontSize: "16px",
            }}
          >
            システム設定
          </Box>
        </Typography>
        {profileColumns.map((column, i) => {
          const authority = loginUser ? loginUser?.authority : "";
          if (column.admin.includes(authority)) {
            return (
              <SystemList
                i={i}
                column={column}
                profileData={profileData}
                setEditSystemConfig={setEditSystemConfig}
              />
            );
          }
        })}
      </Box>
      <SystemConfigEditModal
        onModal={
          editSystemConfig !== null && editSystemConfig.status !== "editing"
        }
        swCloseModal={() => setEditSystemConfig(null)}
        editSystemConfig={editSystemConfig}
        setEditSystemConfig={setEditSystemConfig}
        putEditData={putEditData}
      />
      {aiChatOpen && (
        <AiChatSideBar
          open={aiChatOpen}
          setOpen={setAiChatOpen}
          pageName="SystemConfig"
        />
      )}
    </Box>
  );
}

export default SystemConfig;
